import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchVRCById, deleteVRC, clearVRCDetail } from '../../../Redux/Actions/actions';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import styles from './VRCDetail.module.css';

const VRCDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { vrcDetail, vrcLoading, vrcError } = useSelector(state => state);
  const pdfRef = useRef(null);

  useEffect(() => {
    dispatch(fetchVRCById(id));
    return () => dispatch(clearVRCDetail());
  }, [id, dispatch]);

  const handleDelete = async () => {
    if (!window.confirm('¿Seguro que querés eliminar este VRC?')) return;
    await dispatch(deleteVRC(id));
    navigate('/vrc-lista');
  };

  const handlePDF = async () => {
    if (!pdfRef.current) return;
    const canvas = await html2canvas(pdfRef.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    let posY = 0;
    const pageHeight = pdf.internal.pageSize.getHeight();
    pdf.addImage(imgData, 'PNG', 0, posY, pageWidth, imgHeight);
    let heightLeft = imgHeight - pageHeight;
    while (heightLeft > 0) {
      posY -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, posY, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`VRC_${vrcDetail?.propiedadData?.direccion || id}.pdf`);
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getCoefValue = (comp, key) => {
    if (comp?.useCustom?.[key]) {
      const customValue = parseFloat(comp?.coefCustom?.[key]);
      if (!Number.isNaN(customValue) && customValue > 0) return customValue;
    }
    return parseFloat(comp?.coeficientes?.[key]) || 1;
  };

  const getSemaforoColor = (vrc, aspiracional) => {
    if (!aspiracional || !vrc) return null;
    const ratio = aspiracional / vrc;
    if (ratio <= 1.05) return 'green';
    if (ratio <= 1.15) return 'amber';
    return 'red';
  };

  if (vrcLoading) return <div className={styles.loading}>Cargando informe...</div>;
  if (vrcError) return <div className={styles.error}>Error: {vrcError}</div>;
  if (!vrcDetail) return <div className={styles.loading}>Sin datos</div>;

  const { propiedadData, comparables = [], resultados, foda, analisis, propietario, agente } = vrcDetail;
  const semaforoColor = getSemaforoColor(resultados?.vrcFinal, propiedadData?.precioAspiracional);
  const vrcMin = resultados?.vrcFinal ? resultados.vrcFinal * 0.95 : null;
  const vrcMax = resultados?.vrcFinal ? resultados.vrcFinal * 1.05 : null;
  const comparablesConDatos = comparables.filter(c => c?.precioPublicacion && c?.supHomogeneizada).length;
  const customCoefCount = comparables.reduce((acc, comp) => {
    const keys = Object.keys(comp?.useCustom || {});
    return acc + keys.filter(k => comp?.useCustom?.[k]).length;
  }, 0);

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Link to="/vrc-lista" className={styles.btnBack}>← Volver</Link>
        <div className={styles.toolbarActions}>
          <button onClick={() => navigate(`/vrc-editar/${id}`)} className={styles.btnEdit}>Editar</button>
          <button onClick={handlePDF} className={styles.btnPDF}>⬇ PDF</button>
          <button onClick={handleDelete} className={styles.btnDelete}>Eliminar</button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={pdfRef} className={styles.report}>
        {/* Portada */}
        <div className={styles.cover}>
          <div className={styles.coverLogo}>BY RAÍCES</div>
          <div className={styles.coverBadge}>VRC PRO</div>
          <h1 className={styles.coverTitle}>Valor Real Competitivo</h1>
          <h2 className={styles.coverDireccion}>{propiedadData?.direccion || '-'}</h2>
          <p className={styles.coverZona}>{propiedadData?.zona || '-'}</p>
          {resultados?.vrcFinal && (
            <div className={styles.coverVRC}>
              <span className={styles.coverVRCLabel}>Valor Estimado de Mercado</span>
              <span className={styles.coverVRCNum}>{formatCurrency(resultados.vrcFinal)}</span>
            </div>
          )}
          <div className={styles.coverMeta}>
            {agente?.nombre && <span>Agente: {agente.nombre}</span>}
            {propietario && <span>Propietario: {propietario}</span>}
            <span>{formatDate(vrcDetail.createdAt)}</span>
          </div>
        </div>

        <section className={styles.summaryStrip}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryK}>Método</span>
            <span className={styles.summaryV}>Comparativo de mercado + ajustes</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryK}>Comparables válidos</span>
            <span className={styles.summaryV}>{comparablesConDatos}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryK}>Coeficientes custom</span>
            <span className={styles.summaryV}>{customCoefCount}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryK}>Tipo</span>
            <span className={styles.summaryV}>{propiedadData?.tipoInmueble || '-'}</span>
          </div>
        </section>

        {/* Datos del inmueble */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Datos del Inmueble</h2>
          <div className={styles.dataGrid}>
            <div className={styles.dataItem}><span className={styles.dataLabel}>Tipo</span><span>{propiedadData?.tipo === 'terreno' ? '🌿 Terreno' : '🏠 Edificado'}</span></div>
            <div className={styles.dataItem}><span className={styles.dataLabel}>Tipo específico</span><span>{propiedadData?.tipoInmueble || '-'}</span></div>
            <div className={styles.dataItem}><span className={styles.dataLabel}>Zona</span><span>{propiedadData?.zona || '-'}</span></div>
            {propiedadData?.tipo !== 'terreno' && <>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Sup. Cubierta</span><span>{propiedadData?.supCubierta || '-'} m²</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Sup. Homogeneizada</span><span>{propiedadData?.supHomogeneizada || '-'} m²</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Antigüedad</span><span>{propiedadData?.antiguedad || '-'} años</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Reciclada</span><span>{propiedadData?.esReciclada ? 'Sí' : 'No'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Dormitorios</span><span>{propiedadData?.dormitorios || '-'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Baños</span><span>{propiedadData?.banos || '-'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Cocheras</span><span>{propiedadData?.cocheras || '-'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Estado</span><span>{propiedadData?.estado || '-'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Orientación</span><span>{propiedadData?.orientacion || '-'}</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Piso</span><span>{propiedadData?.piso ?? '-'}</span></div>
            </>}
            {propiedadData?.tipo === 'terreno' && <>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Sup. Total</span><span>{propiedadData?.supTotal || '-'} m²</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Frente</span><span>{propiedadData?.frente || '-'} m</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Fondo</span><span>{propiedadData?.fondo || '-'} m</span></div>
              <div className={styles.dataItem}><span className={styles.dataLabel}>Topografía</span><span>{propiedadData?.topografia || '-'}</span></div>
            </>}
            <div className={styles.dataItem}><span className={styles.dataLabel}>Precio Aspiracional</span><span className={styles.dataHighlight}>{formatCurrency(propiedadData?.precioAspiracional)}</span></div>
          </div>
        </section>

        {/* Comparables */}
        {comparables.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Comparables</h2>
            {comparables.map((comp, idx) => (
              <div key={idx} className={styles.comparable}>
                <h4 className={styles.compTitle}>Comparable {idx + 1}</h4>
                <div className={styles.dataGrid}>
                  <div className={styles.dataItem}><span className={styles.dataLabel}>URL</span><a href={comp.url} target="_blank" rel="noopener noreferrer" className={styles.compLink}>{comp.url || '-'}</a></div>
                  <div className={styles.dataItem}><span className={styles.dataLabel}>Precio</span><span>{formatCurrency(comp.precioPublicacion)}</span></div>
                  <div className={styles.dataItem}><span className={styles.dataLabel}>Sup. Hom.</span><span>{comp.supHomogeneizada || '-'} m²</span></div>
                  {comp.vuAjustado && <div className={styles.dataItem}><span className={styles.dataLabel}>VU Ajustado</span><span className={styles.dataHighlight}>{formatCurrency(comp.vuAjustado)}/m²</span></div>}
                </div>
                <div className={styles.coefList}>
                  {['estado', 'antiguedad', 'negociabilidad', 'tiempoMercado', 'ubicacion', 'categoria', 'acceso', 'amenities'].map(key => (
                    <div key={key} className={styles.coefPill}>
                      <span className={styles.coefPillLabel}>{key}</span>
                      <span className={styles.coefPillValue}>{getCoefValue(comp, key).toFixed(2)}</span>
                      {comp?.useCustom?.[key] && <span className={styles.coefPillTag}>custom</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Resultado VRC */}
        {resultados?.vrcFinal && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Resultado</h2>
            <div className={styles.resultHero}>
              <div className={styles.resultMain}>
                <span className={styles.resultLabel}>Valor Real Competitivo</span>
                <span className={styles.resultValue}>{formatCurrency(resultados.vrcFinal)}</span>
                <span className={styles.resultRange}>
                  Rango: {formatCurrency(vrcMin)} — {formatCurrency(vrcMax)}
                </span>
              </div>
              <div className={styles.resultMetaCard}>
                <span className={styles.resultMetaLabel}>VRC calculado</span>
                <span className={styles.resultMetaValue}>{formatCurrency(resultados?.vrcCalculado)}</span>
                <span className={styles.resultMetaLabel}>VRC final</span>
                <span className={styles.resultMetaValue}>{formatCurrency(resultados?.vrcFinal)}</span>
              </div>
              {semaforoColor && (
                <div className={`${styles.semaforo} ${styles['semaforo_' + semaforoColor]}`}>
                  <span className={styles.semaforoIcon}>
                    {semaforoColor === 'green' ? '✅' : semaforoColor === 'amber' ? '⚠️' : '🔴'}
                  </span>
                  <span className={styles.semaforoText}>
                    {semaforoColor === 'green' ? 'Precio adecuado' : semaforoColor === 'amber' ? 'Precio elevado' : 'Precio muy alto'}
                  </span>
                  <span className={styles.semaforoSub}>vs. aspiracional {formatCurrency(propiedadData?.precioAspiracional)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* FODA */}
        {foda && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Análisis FODA</h2>
            <div className={styles.fodaGrid}>
              {[['Fortalezas', foda.fortalezas, '#0d9e6e'], ['Oportunidades', foda.oportunidades, '#3B3FB5'], ['Debilidades', foda.debilidades, '#f59e0b'], ['Amenazas', foda.amenazas, '#dc2626']].map(([label, text, color]) => (
                <div key={label} className={styles.fodaCell} style={{ borderTopColor: color }}>
                  <h4 style={{ color }}>{label}</h4>
                  <p>{text || '-'}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Análisis narrativo */}
        {analisis && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Análisis del Mercado</h2>
            {analisis.resumenEjecutivo && <div className={styles.analisisBlock}><h4>Resumen Ejecutivo</h4><p>{analisis.resumenEjecutivo}</p></div>}
            {analisis.descripcionMercado && <div className={styles.analisisBlock}><h4>Descripción del Mercado</h4><p>{analisis.descripcionMercado}</p></div>}
            {analisis.recomendaciones && <div className={styles.analisisBlock}><h4>Recomendaciones</h4><p>{analisis.recomendaciones}</p></div>}
            {analisis.descargo && <div className={styles.analisisBlock + ' ' + styles.descargo}><h4>Descargo de Responsabilidad</h4><p>{analisis.descargo}</p></div>}
          </section>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <p>BY RAÍCES — Informe VRC generado el {formatDate(vrcDetail.createdAt)}</p>
          <p>Este informe es de carácter informativo y no constituye tasación oficial.</p>
        </div>
      </div>
    </div>
  );
};

export default VRCDetail;
